import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import { pluralizeToString } from 'lib/string/pluralize'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import ModalContext from 'contexts/ModalContext'
import BusinessTravellerSelectHotelsTravellersModal from './BusinessTravellerSelectHotelsTravellersModal'
import { useAppDispatch } from 'hooks/reduxHooks'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
import { normaliseOccupancies } from 'businessTraveller/utils/normaliseOccupancies'
import BusinessTravellerSelectHotelsRoomsModal from './BusinessTravellerSelectHotelsRoomsModal'
import { searchSteps } from 'components/SearchV2/Components/Mobile/TabContents/HotelsTabContent'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  guestCount: number
}

interface Props {
  className?: string;
  activeStep: searchSteps | undefined
  openEmployeeMenu: () => void
  openRoomMenu: () => void
  openLocationMenu: () => void
}

function BusinessTravellerSelectHotelsMobile(props: Props & MappedStateProps) {
  const {
    activeStep,
    className,
    travellerEmployees,
    guestCount,
    openRoomMenu,
    openLocationMenu,
  } = props

  const state = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const occupancies = useMemo(() => state.occupancies ?? [], [state.occupancies])

  const localInputRef = useRef<HTMLButtonElement>(null)
  const roomInputRef = useRef<HTMLButtonElement>(null)
  const [error, setError] = useState<string>()

  const totalTravellers = travellerEmployees.length + guestCount
  const roomCount = totalTravellers === 0 ? 0 : occupancies.length

  const fieldValue = useMemo(() => {
    if (totalTravellers === 0) {
      return undefined
    }
    const primaryTraveller = travellerEmployees[0]
    if (!primaryTraveller) {
      return `${totalTravellers} guests`
    }
    if (totalTravellers === 1) {
      return `${primaryTraveller.firstName} ${primaryTraveller.lastName}`
    }
    return `${primaryTraveller.firstName} ${primaryTraveller.lastName} +${totalTravellers - 1} more`
  }, [travellerEmployees, totalTravellers])

  // Open room menu after selecting travellers
  const handleSelectTravellerApply = useCallback(() => {
    openRoomMenu()
  }, [openRoomMenu])

  const handleSelectRoomApply = useCallback(() => {
    openLocationMenu()
  }, [openLocationMenu])

  const showModal = useContext(ModalContext)

  const appDispatch = useAppDispatch()

  const setOccupants = useCallback((employeeIds: Array<string>, guestCount: number, roomCount?: number) => {
    const travellerCount = employeeIds.length + guestCount
    appDispatch(setSelectedBusinessTravellersAndGuests(employeeIds, guestCount))
    // Access occupancies from state to avoid stale closure
    const currentOccupancies = state.occupancies ?? []
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: normaliseOccupancies(travellerCount, roomCount ?? Math.min(currentOccupancies.length, travellerCount)) })
    // Save to localstorage so persist on page refresh
    saveBusinessTravellerSelectedTravellerState(employeeIds, guestCount)
  }, [appDispatch, globalSearchDispatch, state.occupancies])

  const showBusinessTravellerSelectTravellersModal = useCallback(async() => {
    await showModal(<BusinessTravellerSelectHotelsTravellersModal setOccupants={setOccupants} onApply={handleSelectTravellerApply} />)
  }, [showModal, setOccupants, handleSelectTravellerApply])

  const showBusinessTravellerSelectRoomsModal = useCallback(async() => {
    await showModal(<BusinessTravellerSelectHotelsRoomsModal onApply={handleSelectRoomApply} />)
  }, [showModal, handleSelectRoomApply])

  useEffect(() => {
    if (activeStep === searchSteps.GUESTS) {
      showBusinessTravellerSelectTravellersModal()
    } else if (activeStep === searchSteps.MULTI_ROOMS) {
      showBusinessTravellerSelectRoomsModal()
    }
  }, [activeStep, showBusinessTravellerSelectTravellersModal, showBusinessTravellerSelectRoomsModal])

  return (
    <SearchFormFieldGroup className={className}>
      <HiddenInput
        value={fieldValue}
        name="travellers"
        required
        invalidMessage="Please select traveller"
        onErrorUpdate={setError}
      />
      <Group direction="horizontal" gap={8} wrap="wrap">
        <Group direction="horizontal" gap={12}>
          <AssistChip
            variant="outline"
            size="medium"
            startIcon={<LineUserIcon />}
            endIcon={<LineAngleDownIcon />}
            onClick={showBusinessTravellerSelectTravellersModal}
            ref={localInputRef}
            error={!!error}
          >
            {fieldValue && <>Travellers: <b>{fieldValue}</b></>}
            {!fieldValue && 'Add travellers'}
          </AssistChip>
        </Group>
        <Group direction="horizontal" gap={12}>

          <AssistChip
            variant="outline"
            size="medium"
            endIcon={<LineAngleDownIcon />}
            onClick={showBusinessTravellerSelectRoomsModal}
            ref={roomInputRef}
          >
            {pluralizeToString('room', roomCount)}
          </AssistChip>
        </Group>
      </Group>
    </SearchFormFieldGroup>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    guestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsMobile)
